/**
 * formMonitor.js
 * Drop this into any page with a form.
 * Automatically detects failures and fires alerts.
 *
 * Usage:
 *   import { monitorHubSpotForm, reportFormError } from '@/lib/formMonitor';
 *   useEffect(() => { monitorHubSpotForm('book-demo'); }, []);
 *
 * Endpoints are PHP files in public/api/ (cPanel compatible). They get
 * copied to out/api/ during build, served at /api/*.php after upload.
 */

const ALERT_ENDPOINT = '/api/form-alert.php';
const HEALTH_CHECK   = '/api/form-health.php';

/* ── Send alert to backend ── */
async function sendAlert(payload) {
  try {
    await fetch(ALERT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        page: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch (e) {
    // Silently fail — alert sending itself should never break the page
    console.error('[FormMonitor] Failed to send alert:', e.message);
  }
}

/* ── Manual error reporter — call this from catch blocks ── */
export async function reportFormError({ form, error, errorType = 'submit_failure' }) {
  console.error(`[FormMonitor] ${errorType} on ${form}:`, error);
  await sendAlert({ form, error: String(error), errorType });
}

/* ── Monitor HubSpot form load + submission ── */
export function monitorHubSpotForm(formName = 'book-demo') {
  if (typeof window === 'undefined') return;

  const LOAD_TIMEOUT_MS  = 12000; // Alert if form doesn't load in 12s
  const SUBMIT_TIMEOUT_MS = 15000; // Alert if submission hangs 15s

  /* 1. Detect if HubSpot script fails to load */
  let formLoaded = false;
  const loadTimer = setTimeout(() => {
    if (!formLoaded) {
      sendAlert({
        form: formName,
        error: 'HubSpot form did not render within 12 seconds. Script may be blocked or portal ID incorrect.',
        errorType: 'load_failure',
      });
    }
  }, LOAD_TIMEOUT_MS);

  /* 2. Listen for HubSpot form ready event */
  window.addEventListener('message', (event) => {
    if (event.data?.type === 'hsFormCallback') {
      if (event.data.eventName === 'onFormReady') {
        formLoaded = true;
        clearTimeout(loadTimer);
      }

      /* 3. Detect submission failure via HubSpot callback */
      if (event.data.eventName === 'onFormSubmitError') {
        sendAlert({
          form: formName,
          error: JSON.stringify(event.data?.data || 'Unknown submission error'),
          errorType: 'submit_failure',
        });
      }

      /* 4. Log successful submissions (no alert, just monitoring) */
      if (event.data.eventName === 'onFormSubmitted') {
        console.log(`[FormMonitor] ✅ ${formName} submitted successfully`);
        // Optionally fire GA4 event
        if (window.gtag) {
          window.gtag('event', 'form_submit', {
            event_category: 'Lead',
            event_label: formName,
          });
        }
      }
    }
  });

  /* 5. Detect if HubSpot JS is script-blocked (ad blockers etc.) */
  setTimeout(() => {
    if (typeof window.hbspt === 'undefined' && !formLoaded) {
      sendAlert({
        form: formName,
        error: 'window.hbspt is undefined — HubSpot script likely blocked by ad blocker or CSP policy.',
        errorType: 'script_blocked',
      });
    }
  }, 8000);

  /* Return cleanup function */
  return () => clearTimeout(loadTimer);
}

/* ── Periodic health check (run once on app mount) ── */
export async function runFormHealthCheck() {
  if (typeof window === 'undefined') return;

  try {
    const res  = await fetch(HEALTH_CHECK);
    const data = await res.json();

    if (data.status === 'degraded') {
      console.warn('[FormMonitor] ⚠️ Form health check failed:', data);
      // Health endpoint already logs server-side — no duplicate alert needed
    }

    return data;
  } catch (e) {
    console.error('[FormMonitor] Health check request failed:', e.message);
  }
}
